// 밤 전투 표현 규칙의 작은 정본.
//
// 판정·피해·스폰은 이 파일을 읽지 않는다. 여러 렌더러가 같은 사건을 서로 다른
// 모양으로 말하되, 화면 전체 채널(플래시·가장자리·카메라)만 같은 우선순위를 쓰게 한다.

const clamp01 = (v) => Math.max(0, Math.min(1, Number(v) || 0));

// 일반 적 행동은 월드가 판정을, 화면이 몸짓과 예고를 소유한다. 이 작은 계약 해석기는
// scene/vfx가 서로 다른 fallback을 만들어 실제 잠금점과 그림이 갈라지는 것을 막는다.
// 객체를 매 프레임 새로 만들지 않도록 호출부가 `out`을 재사용할 수 있다.
export const ENEMY_BEHAVIOR_STATES = Object.freeze([
  'fallback', 'approach', 'tell', 'commit', 'recover',
]);
const ENEMY_BEHAVIOR_STATE_SET = new Set(ENEMY_BEHAVIOR_STATES);

// 전투 중앙은 몸짓을 읽는 자리다. 정예/보스 숫자는 우선 보존하되 물량층은 같은
// 48px 셀·같은 피해값끼리 한 문장으로 묶는다. 월드의 실제 피해 기록은 손대지 않는다.
export const DAMAGE_TEXT_DISPLAY = Object.freeze({ total: 6, small: 3, cell: 48 });

export function compactDamageText(source, out = []) {
  out.length = 0;
  const major = [];
  const grouped = new Map();
  for (const entry of source ?? []) {
    if (!entry?.small) {
      if (entry) major.push(entry);
      continue;
    }
    const x = Number(entry.x) || 0;
    const y = Number(entry.y) || 0;
    const value = Number(entry.v) || 0;
    const key = `${Math.floor(x / DAMAGE_TEXT_DISPLAY.cell)}:`
      + `${Math.floor(y / DAMAGE_TEXT_DISPLAY.cell)}:${value}`;
    const found = grouped.get(key);
    if (!found) {
      grouped.set(key, { ...entry, x, y, v: value, count: 1 });
      continue;
    }
    const count = found.count + 1;
    found.x += (x - found.x) / count;
    found.y += (y - found.y) / count;
    found.t = Math.max(Number(found.t) || 0, Number(entry.t) || 0);
    found.count = count;
  }
  const majorTake = Math.min(DAMAGE_TEXT_DISPLAY.total, major.length);
  const smallTake = Math.min(DAMAGE_TEXT_DISPLAY.small,
    DAMAGE_TEXT_DISPLAY.total - majorTake);
  const small = [...grouped.values()]
    .sort((a, b) => (Number(a.t) || 0) - (Number(b.t) || 0))
    .slice(-smallTake);
  out.push(...major.slice(-majorTake), ...small);
  out.sort((a, b) => (Number(a.t) || 0) - (Number(b.t) || 0));
  return out;
}

// 한 프레임에 여러 무리가 동시에 상태를 바꿔도 귀가 읽을 수 있는 사건은 세 층뿐이다.
// scene은 각 층에서 가장 가까운/중요한 한 개만 고르고 audio의 시간 레이트리밋이
// 연속 프레임 중복을 다시 막는다. 이 값은 오실레이터 수의 상한이기도 하다.
export const BEHAVIOR_AUDIO_BUDGET = Object.freeze({
  danger: 1,   // major tell
  action: 1,   // commit
  recover: 1,  // punish/recovery window
});

const BEHAVIOR_AUDIO_FAMILY = Object.freeze({
  hyena: 'charge',
  jackal: 'intercept',
  'wild-dog': 'pack',
  'wild-dog-leader': 'pack',
  bat: 'wing',
  crow: 'wing',
});
const ATTACK_AUDIO_FAMILY = Object.freeze({
  'bite-retreat': 'bite',
  'orbit-lunge': 'bite',
  'solo-bite': 'pack',
  'crossing-snap': 'intercept',
  'rear-kick': 'kick',
  'swoop-bite': 'wing',
  'alternating-bite': 'pack',
  'pack-command': 'pack',
  'staggered-peck': 'wing',
  'frontal-bite': 'charge',
  'talon-rake': 'talon',
});
const ATTACK_STAGE_SET = new Set(['tell', 'commit', 'recover']);

// `Number(null)`/`Number('')` are both zero. Core intentionally uses null for
// "no locked target", so reject absent values before numeric coercion.
const finite = (value) => value !== null && value !== ''
  && value !== undefined && Number.isFinite(Number(value));
const validBehaviorState = (value) => ENEMY_BEHAVIOR_STATE_SET.has(value);

export function resolveEnemyBehavior(enemy, out = {}) {
  const e = enemy ?? {};
  const hasContract = validBehaviorState(e.behaviorState);
  out.contracted = hasContract;
  const legacyState = (Number(e.tell) || 0) > 0 ? 'tell'
    : (Number(e.dash) || 0) > 0 ? 'commit'
      : (Number(e.recover) || 0) > 0 ? 'recover' : 'approach';
  out.kind = typeof e.behaviorKind === 'string' ? e.behaviorKind : null;
  out.state = hasContract ? e.behaviorState : legacyState;
  out.progress = hasContract ? clamp01(e.behaviorProgress) : 0;
  out.seq = Math.max(0, Number(e.behaviorSeq) | 0);
  out.attackKind = typeof e.attackKind === 'string' ? e.attackKind : null;
  out.attackStage = ATTACK_STAGE_SET.has(e.attackStage) ? e.attackStage : null;
  out.attackActive = out.attackStage !== null;
  out.minor = e.behaviorMinor === true;
  out.token = e.behaviorToken ?? null;
  // 구 하이에나에는 behaviorMajor가 없다. 계약이 들어온 개체는 명시값만 믿고,
  // 구 개체만 tell을 최상위 경보로 승격한다.
  out.major = hasContract ? e.behaviorMajor === true : (Number(e.tell) || 0) > 0;
  out.originX = finite(e.x) ? Number(e.x) : 0;
  out.originY = finite(e.y) ? Number(e.y) : 0;
  out.hasTarget = finite(e.behaviorTargetX) && finite(e.behaviorTargetY);
  out.targetX = out.hasTarget ? Number(e.behaviorTargetX) : NaN;
  out.targetY = out.hasTarget ? Number(e.behaviorTargetY) : NaN;

  let dx = finite(e.behaviorDx) ? Number(e.behaviorDx)
    : finite(e.dx) ? Number(e.dx) : 0;
  let dy = finite(e.behaviorDy) ? Number(e.behaviorDy)
    : finite(e.dy) ? Number(e.dy) : 0;
  // 종점이 있으면 그 벡터가 예고 기하의 정본이다. 별도 방향 필드와 소수점이 달라도
  // 화면은 실제 도착점을 가리킨다.
  const targetDx = out.hasTarget ? out.targetX - out.originX : 0;
  const targetDy = out.hasTarget ? out.targetY - out.originY : 0;
  const targetD = Math.hypot(targetDx, targetDy);
  if (targetD > 1e-6) { dx = targetDx; dy = targetDy; }
  const d = Math.hypot(dx, dy);
  out.dx = d > 1e-6 ? dx / d : 1;
  out.dy = d > 1e-6 ? dy / d : 0;
  const bodyRawX = finite(e.bodyFacingDx) ? Number(e.bodyFacingDx) : out.dx;
  const bodyRawY = finite(e.bodyFacingDy) ? Number(e.bodyFacingDy) : out.dy;
  const bodyD = Math.hypot(bodyRawX, bodyRawY);
  out.bodyDx = bodyD > 1e-6 ? bodyRawX / bodyD : out.dx;
  out.bodyDy = bodyD > 1e-6 ? bodyRawY / bodyD : out.dy;
  const contractDistance = finite(e.behaviorDistance) ? Math.max(0, Number(e.behaviorDistance)) : 0;
  out.distance = out.hasTarget ? targetD : contractDistance;
  out.active = out.attackActive
    || out.state === 'tell' || out.state === 'commit' || out.state === 'recover';
  out.species = e.species ?? e._sp ?? null;
  out.cohortId = e.cohortId ?? null;
  out.cohortRole = e.cohortRole ?? null;
  out.cohortSlot = Number(e.cohortSlot) | 0;
  out.cohortCommand = e.cohortCommand ?? null;
  out.laneId = e.laneId ?? null;
  out.laneSide = Number(e.laneSide) || 0;
  out.wavePhase = finite(e.wavePhase) ? Number(e.wavePhase) : 0;
  out.waveSide = Number(e.waveSide) || 0;
  return out;
}

// 행동음은 enemy 객체에 `_played` 같은 필드를 쓰지 않는다. 렌더 수명에만 존재하는
// WeakMap memory를 호출부가 넘기며, 같은 seq/state를 정확히 한 번만 사건으로 바꾼다.
// tell은 실제 major 또는 직접 공격하지 않는 우두머리의 물리적 명령만 들린다.
// commit/recover는 무리 중 하나가 scene의 거리/우선순위 버킷에서 뽑히므로 수백
// 마리가 같은 프레임에 바뀌어도 세 소리를 넘지 않는다.
export function resolveBehaviorAudioEvent(enemy, memory = {}, out = {}, behavior = null) {
  const b = behavior ?? resolveEnemyBehavior(enemy, out._behavior ?? (out._behavior = {}));
  const stage = b.attackStage ?? b.state;
  const key = `${b.seq}:${b.attackKind ?? b.kind ?? ''}:${stage}`;
  out.active = false;
  out.key = key;
  out.family = ATTACK_AUDIO_FAMILY[b.attackKind]
    ?? BEHAVIOR_AUDIO_FAMILY[b.species] ?? null;
  out.stage = stage;
  out.bucket = stage === 'tell' ? 'danger'
    : stage === 'commit' ? 'action'
      : stage === 'recover' ? 'recover' : null;
  out.species = b.species;
  out.major = b.major;
  out.x = b.originX;
  out.y = b.originY;
  out.seq = b.seq;
  if (memory.key === key) return out;
  memory.key = key;
  memory.seq = b.seq;
  memory.state = stage;

  if (!out.family || !out.bucket) return out;
  const leaderCommand = b.attackKind === 'pack-command'
    && b.species === 'wild-dog-leader' && stage === 'tell';
  if (stage === 'tell' && !b.major && !leaderCommand) return out;
  // A no-tell intercept has to win over ordinary rushes at onset. Major tells
  // remain absolute; recovery is deliberately the quietest information layer.
  const base = stage === 'tell' ? 100 : stage === 'commit' ? 58 : 28;
  const speciesBias = b.species === 'jackal' && stage === 'commit' ? 18
    : b.species === 'crow' ? 10
      : b.species === 'hyena' ? 8
        : b.species === 'wild-dog-leader' ? 7
          : b.species === 'bat' ? -8 : 0;
  out.importance = base + speciesBias;
  out.weight = b.major || b.species === 'wild-dog-leader' ? 1.12
    : b.species === 'bat' ? 0.82 : 1;
  out.active = true;
  return out;
}

// 평상시는 기존 보행 시트를, 실제 공격 창은 전용 4단 공격 시트를 쓴다. 몸의 압축,
// 진행 방향 기울기, 공중 들림도 같은 판정 상태에서만 파생하며 색은 의미 채널로 쓰지 않는다.
export function enemyBehaviorPose(enemy, clock = 0, out = {}, behavior = null) {
  const b = behavior ?? resolveEnemyBehavior(enemy, out._behavior ?? (out._behavior = {}));
  const species = b.species;
  const p = b.progress;
  const beat = Math.sin(Math.PI * p);
  const ease = p * p * (3 - 2 * p);
  // Atlas rows already face left/right. Rotation is the local pitch away from
  // that row's baseline, so the same world dy must invert on a left-facing row.
  const facingSign = b.bodyDx >= 0 ? 1 : -1;
  const localTurn = b.bodyDy * facingSign;
  out.idle = b.state === 'tell' || b.state === 'recover';
  out.scaleX = 1;
  out.scaleY = 1;
  out.rotation = 0;
  out.lift = 0;
  out.shadowStretch = 1;
  out.dust = 0;
  out.trail = 0;
  // Source-cell pixels, applied only by Scene and hard-clamped there.  A tiny
  // fore/aft body shift makes anticipation and recoil legible without moving
  // the world actor, its contact sweep, target lock or damage circle.
  out.forward = 0;
  // 4x2 원본 시트의 열 번호. null이면 기존 보행 위상을 쓴다. 새 도형으로 몸을
  // 대신하지 않고 실제 픽셀 실루엣의 발/날개 자세를 상태 전이에 맞춰 고른다.
  out.frame = null;
  out.attackFrame = null;
  const step3 = Math.floor((Number(clock) || 0) * 9 + b.seq) % 3;
  const stride = Math.abs(Math.sin(Math.PI * ((Number(clock) || 0) * 9 + b.seq)));

  // 전용 공격 시트는 공격 판정과 같은 세 단계만 읽는다. 프레임 0은 몸을 싣는
  // 예비, 1→2는 실제 접촉 경로와 과신전, 3은 무해 회수다. 경고 도형이나 잔상을
  // 덧붙이지 않아도 턱·발·날개가 무엇을 하는지 실루엣 자체로 보이게 한다.
  if (b.attackActive) {
    out.idle = b.attackStage !== 'commit';
    // 우두머리는 직접 들이받지 않고 명령만 내린다. 그래서 pack-command에는 commit이
    // 없으며, tell 한 구간 안에서 준비→짖기→지휘 유지 세 칸을 모두 실제로 보여 준다.
    // 다른 공격은 공통 예비→접촉→과신전→회수 계약을 그대로 따른다.
    out.attackFrame = b.attackKind === 'pack-command' && b.attackStage === 'tell'
      ? (p < 0.22 ? 0 : p < 0.76 ? 1 : 2)
      : b.attackKind === 'rear-kick'
        ? (b.attackStage === 'tell' ? (p < 0.25 ? 0 : 1)
          : b.attackStage === 'commit' ? 2 : 3)
        : b.attackStage === 'tell' ? 0
          : b.attackStage === 'commit' ? (p < 0.52 ? 1 : 2) : 3;
    if (b.attackStage === 'tell') {
      out.scaleX = 1 - 0.04 * ease;
      out.scaleY = 1 + 0.05 * ease;
      out.rotation = -facingSign * 0.035 * ease + localTurn * 0.08;
      out.forward = -0.6 * ease;
    } else if (b.attackStage === 'commit') {
      out.scaleX = 1 + 0.08 * beat;
      out.scaleY = 1 - 0.05 * beat;
      out.rotation = facingSign * 0.035 * beat + localTurn * 0.12;
      out.forward = 0.8 * beat;
    } else {
      out.scaleX = 1 + 0.03 * (1 - p);
      out.scaleY = 1 - 0.02 * (1 - p);
      out.rotation = -facingSign * 0.04 * (1 - p) + localTurn * 0.05;
      out.forward = -0.5 * (1 - p);
    }
    if (species === 'bat') {
      out.lift = b.attackStage === 'tell' ? 4 + 3 * ease
        : b.attackStage === 'commit' ? 4 * (1 - ease)
          : 4 + 5 * beat;
    } else if (species === 'crow') {
      out.lift = b.attackStage === 'tell' ? 3 + 5 * ease
        : b.attackStage === 'commit' ? 7 * (1 - ease)
          : 4 + 7 * beat;
    }
    out.clock = Number(clock) || 0;
    return out;
  }

  if (species === 'hyena') {
    if (b.state === 'tell') {
      out.frame = 0;
      // Stop and load the rear legs: a short, high coil that is the inverse of
      // the long, low commit silhouette. The old 10/12% squeeze made both
      // stages look like the same walk frame at gameplay scale.
      out.scaleX = 1 - 0.18 * ease;
      out.scaleY = 1 + 0.16 * ease;
      out.rotation = -facingSign * (0.025 + 0.075 * ease) + localTurn * 0.045;
      out.forward = -1.2 * ease;
      out.dust = 0.35 + 0.35 * p;
    } else if (b.state === 'commit') {
      out.frame = 1 + step3;
      out.idle = false;
      out.scaleX = 1.21 + 0.05 * stride;
      out.scaleY = 0.82 - 0.04 * stride;
      out.rotation = facingSign * 0.125 + localTurn * 0.080;
      out.forward = 2;
      out.shadowStretch = 1.35;
      out.trail = 1;
    } else if (b.state === 'recover') {
      out.frame = p < 0.34 ? 3 : p < 0.72 ? 1 : 0;
      out.scaleX = 1.14 - 0.14 * p + 0.03 * beat;
      out.scaleY = 0.86 + 0.14 * p - 0.02 * beat;
      out.rotation = -facingSign * 0.14 * (1 - p)
        - localTurn * 0.06 * (1 - p);
      out.forward = -1.7 * (1 - p);
      out.shadowStretch = 1.18;
      out.dust = 0.85 * (1 - p);
    }
  } else if (species === 'jackal') {
    if (b.state === 'tell') {
      out.frame = 0;
      out.scaleX = 1 - 0.14 * ease;
      out.scaleY = 1 + 0.12 * ease;
      out.rotation = -facingSign * 0.085 * ease + localTurn * 0.050;
      out.forward = -1 * ease;
      out.dust = 0.28 + 0.28 * p;
    } else if (b.state === 'commit') {
      out.frame = 1 + step3;
      out.idle = false;
      // Jackal has no held tell: the first committed frame must itself read as
      // an intercept rather than another ordinary walk cycle.
      out.scaleX = 1.18 + 0.05 * stride;
      out.scaleY = 0.84 - 0.03 * stride;
      out.rotation = facingSign * 0.11 + localTurn * 0.070;
      out.forward = 1.8;
      out.shadowStretch = 1.22;
      out.trail = 0.72;
    } else if (b.state === 'recover') {
      out.frame = p < 0.38 ? 3 : p < 0.72 ? 1 : 0;
      out.scaleX = 1.11 - 0.11 * p + 0.025 * beat;
      out.scaleY = 0.88 + 0.12 * p - 0.02 * beat;
      out.rotation = -facingSign * 0.12 * (1 - p)
        - localTurn * 0.045 * (1 - p);
      out.forward = -1.4 * (1 - p);
      out.dust = 0.52 * (1 - p);
    }
  } else if (species === 'wild-dog' || species === 'wild-dog-leader') {
    const rally = b.cohortCommand === 'rally';
    const rush = b.cohortCommand === 'rush' || b.state === 'commit';
    const recovery = b.cohortCommand === 'recover' || b.state === 'recover';
    if (rally) {
      out.frame = b.cohortRole === 'leader' ? 0 : 3;
      out.idle = true;
      if (b.cohortRole === 'leader') {
        // Leader rises and throws its head back; members do the opposite and
        // load their rear legs. The command therefore reads from silhouettes,
        // even with every marker hidden.
        out.scaleX = 0.92 - 0.07 * ease;
        out.scaleY = 1.12 + 0.16 * ease;
        out.rotation = -facingSign * (0.055 + 0.055 * ease)
          - localTurn * 0.025;
        out.forward = -0.6 * ease;
      } else {
        out.scaleX = 1 - 0.12 * ease;
        out.scaleY = 1 + 0.08 * ease;
        out.rotation = -facingSign * (0.025 + 0.045 * ease)
          + localTurn * 0.035;
        out.forward = -1 * ease;
      }
      out.dust = 0.34 + 0.28 * p;
    } else if (rush) {
      out.frame = 1 + step3;
      out.idle = false;
      const leader = b.cohortRole === 'leader';
      out.scaleX = (leader ? 1.18 : 1.21) + 0.04 * stride;
      out.scaleY = (leader ? 0.84 : 0.82) - 0.03 * stride;
      out.rotation = facingSign * (leader ? 0.105 : 0.125)
        + localTurn * 0.070;
      out.forward = leader ? 1.7 : 2;
      out.shadowStretch = 1.28;
      out.trail = 0.82;
    } else if (recovery) {
      out.frame = p < 0.35 ? 3 : p < 0.70 ? 1 : 0;
      out.idle = true;
      out.scaleX = 1.13 - 0.13 * p + 0.025 * beat;
      out.scaleY = 0.86 + 0.14 * p - 0.02 * beat;
      out.rotation = -facingSign * 0.115 * (1 - p)
        - localTurn * 0.045 * (1 - p);
      out.forward = -1.4 * (1 - p);
      out.dust = 0.62 * (1 - p);
    }
  } else if (species === 'bat') {
    // wavePhase는 코어가 라디안 0..2π로 준다. 다시 정규화하면 실제 횡파동과 어긋난다.
    const wave = Math.sin(b.wavePhase) * (b.waveSide || 1);
    const phase = ((b.wavePhase % (Math.PI * 2)) + Math.PI * 2) % (Math.PI * 2);
    out.frame = Math.floor(phase / (Math.PI * 0.5)) % 4;
    out.idle = false;
    out.rotation = wave * facingSign * 0.19;
    out.lift = 3 + Math.abs(Math.cos(b.wavePhase)) * 4;
    const wingOpen = 0.5 + Math.cos(phase) * 0.5;
    out.scaleX = 0.88 + wingOpen * 0.24;
    out.scaleY = 1.16 - wingOpen * 0.24;
    out.shadowStretch = 0.86 + Math.abs(wave) * 0.24;
    out.trail = Math.abs(wave) * 0.28;
  } else if (species === 'crow') {
    if (b.state === 'tell') {
      out.frame = p < 0.48 ? 1 : 3;
      out.idle = true;
      out.scaleX = 0.84 + 0.08 * ease;
      out.scaleY = 1.25 - 0.12 * ease;
      out.rotation = -facingSign * (0.075 + 0.035 * ease)
        + localTurn * 0.055;
      out.lift = 3 + 5 * ease;
    } else if (b.state === 'commit') {
      out.frame = (Math.floor((Number(clock) || 0) * 12 + b.seq) & 1) ? 0 : 2;
      out.idle = false;
      out.scaleX = 1.24;
      out.scaleY = 0.78;
      out.rotation = facingSign * 0.145 + localTurn * 0.12;
      // The body descends through its committed lane instead of floating at a
      // fixed height for the whole dive.
      out.lift = 8 - 7 * ease;
      out.forward = 1;
      out.shadowStretch = 1.42;
      out.trail = 1;
    } else if (b.state === 'recover') {
      out.frame = p < 0.55 ? 3 : 1;
      out.idle = false;
      out.scaleX = 1.12 - 0.12 * p;
      out.scaleY = 0.94 + 0.06 * p;
      out.rotation = -facingSign * 0.17 * (1 - p)
        - localTurn * 0.08 * (1 - p);
      out.lift = 4 + 7 * beat;
      out.forward = -1 * (1 - p);
      out.shadowStretch = 1.15 - 0.15 * p;
      out.trail = 0.42 * (1 - p);
    }
  } else if (species === 'grasshopper') {
    // World already owns the four phase frames and the leap arc.  This layer
    // only exaggerates the body shape around the same state boundaries.
    if (b.state === 'tell') {
      out.frame = 1;
      out.scaleX = 1 + 0.18 * ease;
      out.scaleY = 1 - 0.34 * ease;
      out.rotation = facingSign * 0.065 * ease + localTurn * 0.035;
      out.forward = -1 * ease;
    } else if (b.state === 'commit') {
      out.frame = 2;
      out.idle = false;
      out.scaleX = 0.88 + 0.06 * beat;
      out.scaleY = 1.18 - 0.08 * beat;
      out.rotation = facingSign * 0.14 + localTurn * 0.09;
      out.forward = 1.2;
    } else if (b.state === 'recover') {
      out.frame = 3;
      out.scaleX = 1.22 - 0.22 * ease;
      out.scaleY = 0.72 + 0.28 * ease;
      out.rotation = -facingSign * 0.10 * (1 - p)
        - localTurn * 0.035 * (1 - p);
      out.forward = -0.8 * (1 - p);
    }
  }
  // clock is deliberately not used for state timing; all meaningful motion follows world fields.
  out.clock = Number(clock) || 0;
  return out;
}

export function enemyWarningProgress(enemy, legacyTellDuration = 1) {
  const e = enemy ?? {};
  if (validBehaviorState(e.behaviorState)) {
    return e.behaviorMajor === true && e.behaviorState === 'tell'
      ? clamp01(e.behaviorProgress) : -1;
  }
  const tell = Number(e.tell) || 0;
  return tell > 0
    ? clamp01(1 - tell / Math.max(1e-6, Number(legacyTellDuration) || 1))
    : -1;
}

const BOSS_ATTACK_SHAPES = new Set([
  'rush', 'paw-pair', 'rush-chain',
  'pounce-rake', 'claw-pair', 'bound-chain',
]);
const BOSS_ATTACK_STAGES = new Set(['signal', 'commit', 'recovery']);
export const BOSS_ATTACK_POINT_MAX = 8;
export const BOSS_ATTACK_LIMB_MAX = 12;

// boss.js가 계산한 판정 기하를 손대지 않고 유한값만 검증해 Graphics에 넘긴다.
// 잘못된/반쪽 기하는 그리지 않는다. 화면이 근삿값으로 공격을 꾸며내는 것보다 안전하다.
export function resolveBossAttackGeometry(boss, out = {}) {
  const source = boss?.attackGeometry;
  out.active = false;
  out.visible = false;
  out.points ??= [];
  out.paws ??= [];
  out.claws ??= [];
  out.strikes ??= [];
  if (!source || !BOSS_ATTACK_SHAPES.has(source.shape)
      || !BOSS_ATTACK_STAGES.has(source.stage)
      || !finite(source.origin?.x) || !finite(source.origin?.y)) {
    out.points.length = 0;
    out.paws.length = 0;
    out.claws.length = 0;
    out.strikes.length = 0;
    return out;
  }

  out.seq = Math.max(0, Number(source.seq) | 0);
  // `active` is collision activity, not presentation visibility. Telegraphs
  // deliberately carry active=false and are still the most important stage to draw.
  out.active = source.active === true;
  out.kind = source.kind ?? null;
  out.shape = source.shape;
  out.stage = source.stage;
  out.originX = Number(source.origin.x);
  out.originY = Number(source.origin.y);
  let fx = finite(source.facing?.x) ? Number(source.facing.x) : 1;
  let fy = finite(source.facing?.y) ? Number(source.facing.y) : 0;
  const fd = Math.hypot(fx, fy);
  if (fd <= 1e-6) { fx = 1; fy = 0; }
  else { fx /= fd; fy /= fd; }
  out.facingX = fx;
  out.facingY = fy;
  out.range = finite(source.range) ? Math.max(0, Number(source.range)) : 0;
  out.bodyRadius = finite(source.bodyRadius) ? Math.max(0, Number(source.bodyRadius)) : 0;
  out.hopIndex = Math.max(0, Number(source.hopIndex) | 0);
  out.hopCount = Math.max(0, Number(source.hopCount) | 0);
  out.progress = clamp01(source.progress);
  let pointCount = 0;
  for (const point of source.points ?? []) {
    if (pointCount >= BOSS_ATTACK_POINT_MAX) break;
    if (finite(point?.x) && finite(point?.y)) {
      const target = out.points[pointCount] ?? (out.points[pointCount] = { x: 0, y: 0 });
      target.x = Number(point.x);
      target.y = Number(point.y);
      pointCount++;
    }
  }
  out.points.length = pointCount;

  let pawCount = 0;
  for (const paw of source.paws ?? []) {
    if (pawCount >= BOSS_ATTACK_LIMB_MAX) break;
    if (!finite(paw?.x) || !finite(paw?.y) || !finite(paw?.r)) continue;
    const target = out.paws[pawCount] ?? (out.paws[pawCount] = {});
    target.x = Number(paw.x); target.y = Number(paw.y);
    target.r = Math.max(0, Number(paw.r));
    target.side = Number(paw.side) || 0;
    target.pointIndex = Math.max(0, Number(paw.pointIndex) | 0);
    target.activeFrom = clamp01(paw.activeFrom);
    target.activeTo = clamp01(paw.activeTo);
    pawCount++;
  }
  out.paws.length = pawCount;

  let clawCount = 0;
  for (const claw of source.claws ?? []) {
    if (clawCount >= BOSS_ATTACK_LIMB_MAX) break;
    if (!finite(claw?.ax) || !finite(claw?.ay)
        || !finite(claw?.bx) || !finite(claw?.by) || !finite(claw?.r)) continue;
    const target = out.claws[clawCount] ?? (out.claws[clawCount] = {});
    target.ax = Number(claw.ax); target.ay = Number(claw.ay);
    target.bx = Number(claw.bx); target.by = Number(claw.by);
    target.r = Math.max(0, Number(claw.r));
    target.side = Number(claw.side) || 0;
    target.pointIndex = Math.max(0, Number(claw.pointIndex) | 0);
    target.activeFrom = clamp01(claw.activeFrom);
    target.activeTo = clamp01(claw.activeTo);
    clawCount++;
  }
  out.claws.length = clawCount;

  let strikeCount = 0;
  for (const strike of source.strikes ?? []) {
    if (strikeCount >= BOSS_ATTACK_POINT_MAX) break;
    if (strike?.kind !== 'paws' && strike?.kind !== 'claws') continue;
    const target = out.strikes[strikeCount] ?? (out.strikes[strikeCount] = {});
    target.kind = strike.kind;
    target.pointIndex = Math.max(0, Number(strike.pointIndex) | 0);
    target.activeFrom = clamp01(strike.activeFrom);
    target.activeTo = clamp01(strike.activeTo);
    strikeCount++;
  }
  out.strikes.length = strikeCount;

  const route = out.points.length >= 2;
  const shapeComplete = source.shape === 'rush' ? route
    : source.shape === 'paw-pair' ? out.paws.length >= 2
      : source.shape === 'rush-chain' ? route && out.paws.length >= 2
        : source.shape === 'pounce-rake' ? route && out.claws.length >= 2
          : source.shape === 'claw-pair' ? out.claws.length >= 2
            : route && out.claws.length >= 2;
  out.visible = shapeComplete;
  return out;
}

// 물맷돌의 두 번째 성장 표현은 판정값을 읽기만 한다. 잔상은 기존 최대 네 복사 안에서
// 등급별로 열리고, 접촉 띠와 눈금은 world가 이미 남긴 0.18초 사건만 번역한다.
export const ORBIT_CONTACT_FEEDBACK_SEC = 0.18;
export const ORBIT_GHOST_MAX = 4;
export const ORBIT_HIT_TICK_MAX = 4;

export function orbitGhostCount(tier) {
  const t = Math.max(1, Math.min(6, Number(tier) | 0));
  return Math.min(ORBIT_GHOST_MAX, Math.ceil((t + 1) / 2));
}

export function orbitContactFeedback(age, radius, band, hits) {
  const elapsed = Number(age);
  const outerR = Math.max(0, Number(radius) || 0);
  const bandRatio = clamp01(band);
  const visible = outerR > 0 && Number.isFinite(elapsed)
    && elapsed >= 0 && elapsed < ORBIT_CONTACT_FEEDBACK_SEC;
  return {
    visible,
    progress: visible ? clamp01(elapsed / ORBIT_CONTACT_FEEDBACK_SEC) : 1,
    outerR,
    // world.orbitStrike와 같은 식. 그림용 근삿값이 아니라 실제 전량 피해 대역의 안쪽 경계다.
    innerR: outerR * (1 - bandRatio),
    bandRatio,
    ticks: visible
      ? Math.max(0, Math.min(ORBIT_HIT_TICK_MAX, Number(hits) | 0))
      : 0,
  };
}

// GearShop.preview()가 실제 적용식과 슬롯별 중요도 순서로 정리한 첫 행을 카드용
// 한 줄로만 접는다. 서로 단위가 다른 변화량에 임의의 크기 점수를 만들지 않는다.
export function candidateLeadChange(previewRows) {
  const row = Array.isArray(previewRows) ? previewRows[0] : null;
  if (!row) return null;
  return {
    ...row,
    text: `${row.arrow} ${row.ko} · ${row.delta}`,
  };
}

export function bossWarningProgress(boss) {
  if (!boss || boss.dead) return -1;
  const geometry = boss.attackGeometry;
  if (geometry?.stage === 'signal') return clamp01(geometry.progress);
  return -1;
}

export const SCREEN_RESPONSE_PRIORITY = Object.freeze({
  environment: 10,
  normal: 20,
  combo: 30,
  elite: 40,
  diamond: 40,
  boss: 50,
  warning: 60,
  hurt: 60,
});

// 6성 돌은 기존 청백색·궤도 효과 위에 각각 다른 동작 문장을 하나씩 더 갖는다.
// 이 표는 누락 검사용이며 실제 그리기는 각 효과가 원래 사는 레이어에서 한다.
export const DIAMOND_SIGNATURES = Object.freeze({
  jandol: 'fan-pulse',
  goreun: 'physical-line-lead',
  napjak: 'skip-phrases-8x4',
  monan: 'shard-generations',
  chadol: 'orbit-contact-contraction',
  mugeo: 'impact-ring-afterglow',
});

export const DIAMOND_PART_PROOFS = Object.freeze({
  cradle: 'socket-fan',
  cord: 'outer-broken-arc',
  loop: 'travelling-glint',
});

export function diamondSignature(stoneId, tier) {
  return (tier | 0) >= 6 ? (DIAMOND_SIGNATURES[stoneId] ?? null) : null;
}

// ◆의 새 문장은 밝기 한 번이 아니라 「본 타격 → 짧게 늦은 굴절 잔향」 두 박자다.
// 화면 전체가 아니라 무기·투사체·착탄점 안에서만 이 값을 소비한다.
export const DIAMOND_ECHO_DELAY = 0.13;
export function diamondBeat(age, mainLife = 0.28, echoLife = 0.42) {
  const t = Number(age);
  if (!Number.isFinite(t) || t < 0) return { main: 0, echo: 0, total: 0 };
  const beat = (v, life) => v >= 0 && v < life ? (1 - v / life) ** 2 : 0;
  const main = beat(t, Math.max(0.01, Number(mainLife) || 0.28));
  const echo = beat(t - DIAMOND_ECHO_DELAY, Math.max(0.01, Number(echoLife) || 0.42));
  return { main, echo, total: clamp01(main + echo * 0.82) };
}

// 코어의 정식 훅은 `setDiamond`. 병렬 배선 중에도 실제 네 칸 ◆가 모두 같은 세트면
// 그림이 빠지지 않도록 payload와 기존 슬롯 목록을 차례로 읽는다.
export function isDiamondSetPresentation(mods) {
  if (!mods?.setId) return false;
  if (mods.setDiamond) return true;
  if (mods.set?.level === 'diamond' || mods.setLevel === 'diamond') return true;
  return Array.isArray(mods.diamondSlots) && mods.diamondSlots.length >= 4;
}

// 위험 예고는 언제나 보상 이펙트보다 앞선다. ◆를 지우지는 않되 경보 중에는 국소
// 발광만 뒤로 물려 조준선·수렴 고리가 먼저 읽히게 한다.
export function diamondLocalAlpha(world) {
  // 한 렌더 프레임 안에서 여러 ◆ 채널이 같은 위험 목록을 읽는다. 장면이 제공하는
  // 아트 시계를 키로 삼아 적 배열은 프레임당 한 번만 훑는다.
  const frame = world?._presentationFrame;
  if (frame !== undefined && world?._diamondLocalAlphaFrame === frame) {
    return world._diamondLocalAlphaValue;
  }
  const enemyTell = (world?.enemies ?? []).some((enemy) => enemyWarningProgress(enemy) >= 0);
  const boss = world?.boss;
  const bossTell = !!boss && !boss.dead && (
    !!boss.target || boss.state === 'tell' || (boss.attackGauge ?? 0) > 0.72
  );
  const value = enemyTell || bossTell ? 0.18 : 1;
  if (world && frame !== undefined) {
    world._diamondLocalAlphaFrame = frame;
    world._diamondLocalAlphaValue = value;
  }
  return value;
}

// 같은 프레임에 여러 사건이 와도 화면 전체 플래시는 하나만 고른다. 개별 타격광,
// 처치 파편, 콤보 선, 번개 볼트는 이 함수 바깥에서 그대로 그려져 사라지지 않는다.
export function chooseScreenResponse(candidates = []) {
  let best = null;
  for (const candidate of candidates) {
    const kind = candidate?.kind;
    const strength = clamp01(candidate?.strength);
    if (!kind || strength <= 0) continue;
    const priority = SCREEN_RESPONSE_PRIORITY[kind] ?? 0;
    if (!best || priority > best.priority
        || (priority === best.priority && strength > best.strength)) {
      best = { ...candidate, kind, strength, priority };
    }
  }
  return best;
}

// 32번을 모두 남기되 8개씩 네 구절로 읽히게 한다. 0이면 평범한 자국이다.
export function diamondSkipAccent(markNo, total) {
  const n = markNo | 0;
  return (total | 0) >= 32 && n > 0 && n <= 32 && n % 8 === 0 ? n / 8 : 0;
}

// 카메라는 사건마다 다른 운동을 쓴다. 반환값은 SHAKE.px를 곱하기 전의 정규화 좌표다.
// 6성은 카메라보다 무기 자체 반응이 주역이므로 의도적으로 진폭을 작게 둔다.
export function cameraOffset(kind, age, power, dx = 0, dy = 0) {
  const t = Math.max(0, Number(age) || 0);
  const p = Math.max(0, Number(power) || 0);
  const d = Math.hypot(dx, dy);
  const nx = d > 1e-5 ? dx / d : 1;
  const ny = d > 1e-5 ? dy / d : 0;
  const tx = -ny, ty = nx;

  if (kind === 'hurt') {
    const e = Math.exp(-t * 12) * p;
    const kick = Math.cos(t * 72) * e;
    return { x: nx * kick + tx * Math.sin(t * 101) * e * 0.18,
             y: ny * kick + ty * Math.sin(t * 101) * e * 0.18 };
  }
  if (kind === 'boss') {
    const e = Math.exp(-t * 5.2) * p;
    return { x: Math.sin(t * 21) * e * 0.68 + nx * Math.cos(t * 13) * e * 0.24,
             y: Math.cos(t * 16) * e * 0.92 + ny * Math.cos(t * 13) * e * 0.24 };
  }
  if (kind === 'elite') {
    const e = Math.exp(-t * 9) * p;
    return { x: Math.sin(t * 54) * e * 0.62, y: Math.cos(t * 47) * e * 0.62 };
  }
  if (kind === 'diamond') {
    const e = Math.exp(-t * 11) * p * 0.42;
    const recoil = -Math.cos(t * 38) * e;
    return { x: nx * recoil + tx * Math.sin(t * 58) * e * 0.12,
             y: ny * recoil + ty * Math.sin(t * 58) * e * 0.12 };
  }
  const e = Math.exp(-t * 8) * p;
  return { x: Math.sin(t * 61) * e * 0.72, y: Math.cos(t * 83.57) * e * 0.72 };
}
